<template>
  <div class="categories-container">
    <h1 class="page-title">分类 | Categories</h1>

    <!-- 分类标签云 -->
    <div class="categories-cloud">
      <div v-for="category in categories"
           :key="category.id"
           class="category-item"
           :class="{ 'active': selectedCategoryId === category.id }"
           @click="showCategoryArticles(category.id)">
        <div class="category-content">
          {{ category.name }}

          <!-- 登录用户才显示的悬浮操作菜单 -->
          <div v-if="isLogin" class="category-actions">
            <el-dropdown trigger="hover" @command="handleCommand($event, category)">
              <span class="el-dropdown-link">
                <i class="el-icon-more"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="edit">
                  <i class="el-icon-edit"></i> 编辑
                </el-dropdown-item>
                <el-dropdown-item command="delete" divided>
                  <i class="el-icon-delete"></i> 删除
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>

      <!-- 登录用户才显示的添加分类按钮 -->
      <div v-if="isLogin" class="add-category" @click="showAddDialog">
        <i class="el-icon-plus"></i>
      </div>
    </div>

    <!-- 分类下的文章列表 -->
    <div v-if="selectedCategoryId && articles.length > 0" class="articles-container">
      <h2 class="category-title">{{ selectedCategoryName }} 下的文章</h2>
      <ArticleList
        :articles="articles"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @page-change="handlePageChange"
        @category-click="handleCategoryClick" />
    </div>

    <div v-else-if="selectedCategoryId && articles.length === 0" class="no-articles">
      <p>该分类下暂无文章</p>
    </div>

    <!-- 添加分类对话框 -->
    <el-dialog title="添加分类" :visible.sync="addDialogVisible" width="30%">
      <el-form :model="categoryForm" :rules="rules" ref="categoryForm">
        <el-form-item prop="name">
          <el-input v-model="categoryForm.name" placeholder="请输入分类名称"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAdd">确定</el-button>
      </span>
    </el-dialog>

    <!-- 编辑分类对话框 -->
    <el-dialog title="编辑分类" :visible.sync="editDialogVisible" width="30%">
      <el-form :model="editForm">
        <el-form-item label="分类名称">
          <el-input v-model="editForm.name"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmEdit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getCategories, addCategory, editCategory, deleteCategory, getArticlesByCategory } from '@/api'
import ArticleList from '@/components/ArticleList.vue'

export default {
  name: 'Categories',
  components: {
    ArticleList
  },
  data() {
    return {
      isLogin: false,
      categories: [],
      categoryForm: {
        name: ''
      },
      rules: {
        name: [
          { required: true, message: '请输入分类名称', trigger: 'blur' }
        ]
      },
      addDialogVisible: false,
      editDialogVisible: false,
      editForm: {
        id: null,
        name: ''
      },
      // 文章列表相关数据
      selectedCategoryId: null,
      selectedCategoryName: '',
      articles: [],
      total: 0,
      pageSize: 10,
      currentPage: 1
    }
  },
  created() {
    this.checkLoginStatus()
    this.fetchCategories()

    // 检查URL参数中是否有分类ID
    const categoryId = this.$route.query.id
    if (categoryId) {
      this.selectedCategoryId = parseInt(categoryId)
      this.fetchArticlesByCategory()
    }
  },
  methods: {
    checkLoginStatus() {
      this.isLogin = !!localStorage.getItem('token')
    },
    async fetchCategories() {
      try {
        const res = await getCategories()
        this.categories = res.data || []

        // 如果已选择分类，找到分类名称
        if (this.selectedCategoryId) {
          const selectedCategory = this.categories.find(c => c.id === this.selectedCategoryId)
          if (selectedCategory) {
            this.selectedCategoryName = selectedCategory.name
          }
        }
      } catch (error) {
        console.error('获取分类列表错误:', error)
        this.$message.error('获取分类列表失败')
      }
    },
    showAddDialog() {
      this.addDialogVisible = true
      this.categoryForm.name = ''
    },
    async handleAdd() {
      try {
        await this.$refs.categoryForm.validate()
        await addCategory(this.categoryForm)
        this.$message.success('添加成功')
        this.addDialogVisible = false
        await this.fetchCategories()
      } catch (error) {
        if (error !== false) {
          console.error('添加分类错误:', error)
          this.$message.error('添加分类失败')
        }
      }
    },
    handleCommand(command, category) {
      if (command === 'edit') {
        if (!category || !category.id) {
          this.$message.error('分类信息不完整')
          return
        }
        this.editForm = {
          id: parseInt(category.id),
          name: category.name
        }
        this.editDialogVisible = true
      } else if (command === 'delete') {
        this.handleDelete(category.id)
      }
    },
    async confirmEdit() {
      try {
        if (!this.editForm.id) {
          throw new Error('分类ID不能为空')
        }
        await editCategory(this.editForm.id, {
          name: this.editForm.name.trim()
        })
        this.$message.success('编辑成功')
        this.editDialogVisible = false
        await this.fetchCategories()

        // 如果编辑的是当前选中的分类，更新分类名称
        if (this.selectedCategoryId === this.editForm.id) {
          this.selectedCategoryName = this.editForm.name.trim()
        }
      } catch (error) {
        console.error('编辑分类错误:', error)
        this.$message.error(error.message || '编辑分类失败')
      }
    },
    async handleDelete(id) {
      try {
        await this.$confirm('确认删除该分类吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteCategory(id)
        this.$message.success('删除成功')
        await this.fetchCategories()

        // 如果删除的是当前选中的分类，清空文章列表
        if (this.selectedCategoryId === id) {
          this.clearArticleList()
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除分类错误:', error)
          this.$message.error('删除分类失败')
        }
      }
    },
    // 显示分类下的文章
    showCategoryArticles(categoryId) {
      if (this.selectedCategoryId === categoryId) {
        // 如果点击的是当前已选中的分类，则取消选择
        this.clearArticleList()
      } else {
        this.selectedCategoryId = categoryId
        const selectedCategory = this.categories.find(c => c.id === categoryId)
        if (selectedCategory) {
          this.selectedCategoryName = selectedCategory.name
        }
        this.currentPage = 1
        this.fetchArticlesByCategory()

        // 更新URL，不刷新页面
        this.$router.push({
          path: '/categories',
          query: { id: categoryId }
        })
      }
    },
    // 获取分类下的文章列表
    async fetchArticlesByCategory() {
      if (!this.selectedCategoryId) return

      try {
        const res = await getArticlesByCategory(this.selectedCategoryId, {
          pageSize: this.pageSize,
          pageNum: this.currentPage
        })

        this.articles = res.data || []
        this.total = res.total || 0
      } catch (error) {
        console.error('获取分类文章列表错误:', error)
        this.$message.error('获取分类文章列表失败')
      }
    },
    // 分页切换
    handlePageChange(page) {
      this.currentPage = page
      this.fetchArticlesByCategory()
    },
    // 清空文章列表
    clearArticleList() {
      this.selectedCategoryId = null
      this.selectedCategoryName = ''
      this.articles = []
      this.total = 0
      this.currentPage = 1

      // 更新URL，移除id参数
      this.$router.push('/categories')
    },
    // 格式化日期
    formatDate(date) {
      return new Date(date).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    // 点击分类标签
    handleCategoryClick(category) {
      if (category) {
        this.$router.push(`/categories?id=${category.id}`)
      }
    }
  }
}
</script>

<style scoped>
.categories-container {
  width: min(calc(100% - 2rem), var(--reading-width));
  margin: 0 auto;
  padding: 2rem 0;
}

.page-title {
  text-align: center;
  margin-bottom: 3rem;
  color: var(--text-color);
  font-size: 2rem;
}

.categories-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  padding: 2rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
}

.category-item {
  position: relative;
  color: #6b5f4c;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: #e8e1d5;
}

.category-item:hover {
  background-color: #e0d9cd;
  transform: translateY(-2px);
}

.category-item.active {
  background-color: #6b5f4c;
  color: #fff;
}

.category-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.category-actions {
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 0.5rem;
}

.category-item:hover .category-actions {
  opacity: 1;
}

.el-dropdown-link {
  cursor: pointer;
  font-size: 1rem;
  color: inherit;
}

.add-category {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e8e1d5;
  color: #6b5f4c;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-category:hover {
  background: #e0d9cd;
  transform: translateY(-2px);
}

/* 文章列表样式 */
.articles-container {
  margin-top: 2rem;
}

.category-title {
  margin-bottom: 1.5rem;
  color: var(--text-color);
  font-size: 1.5rem;
}

.no-articles {
  text-align: center;
  margin-top: 2rem;
  padding: 2rem;
  background: var(--card-bg);
  border-radius: 12px;
  color: var(--text-secondary);
}


</style>
